import { useEffect, useState } from 'react';
import {
  Badge,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import { formatDistanceToNow } from 'date-fns';
import { FoldersIcon } from 'lucide-react';
import {
  PROJECT_TYPE_ICONS,
  PROJECT_TYPE_MAP,
  type ProjectListItem,
  type ProjectType,
} from '@/entities/project';
import { useCurrentUser, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { PaginationControls } from '@/shared/ui';
import { PreResolvedAvatarCell, redesignTableStyles } from '@/shared/ui/data-table';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { TABLE_COL_WIDTH, TABLE_NAME_COL_NO_SIDEBAR } from '@/shared/constants';
import { useModalState } from '@/shared/hooks';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { formatShortDate } from '@/shared/utils/format';
import { ProjectRowActions } from './ProjectRowActions';
import type { ProjectsTableEditDeps } from './ProjectsTable.types';
import { ProjectsTableSkeletonRow } from './ProjectsTableSkeletonRow';
import { useTranslation } from 'react-i18next';

interface ProjectsTableProps {
  rows: ProjectListItem[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onRowClick: (projectId: string) => void;
  onPageChange: (page: number) => void;
  /**
   * Phase D dep-injection bag for the project-edit concerns
   * (`QuickEditProjectModal` + `useDeletePublishedProject`). Wired by the
   * consuming page (`pages/Projects`) so this widget stays decoupled
   * from `features/edit-project`.
   */
  editProject: ProjectsTableEditDeps;
}

const getProjectTypeConfig = (type: ProjectType) => ({
  icon: PROJECT_TYPE_ICONS[type],
  label: PROJECT_TYPE_MAP[type],
});

const SKELETON_ROW_COUNT = 5;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

/**
 * Projects data table matching the Beat UI "Project overview" design.
 *
 * Migrated to monks-ui `Datatable*` directly (MFINS-2897 Option-A pivot):
 * the `border-separate` container comes from `<Datatable>` itself; the
 * header divider is `border-b-2 border-border` at the `<DatatableHead>` level
 * (semantic token, replaces the `border-black/19` opacity hack). Row hover /
 * active states come from the `interactive` variant on `<DatatableRow>`.
 * `aria-busy` is the composite gate `isLoading && rows.length === 0` per D5.
 */
export function ProjectsTable({
  rows,
  isLoading,
  totalCount,
  page,
  rowsPerPage,
  onRowClick,
  onPageChange,
  editProject,
}: ProjectsTableProps) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const currentUser = useCurrentUser();
  const { isWorkspaceAdmin } = usePermissions();
  const workspaceId = useCurrentWorkspaceId();
  const [projectToDelete, setProjectToDelete] = useState<ProjectListItem | null>(null);
  const [projectToEdit, setProjectToEdit] = useState<ProjectListItem | null>(null);
  const editModal = useModalState();

  // Cleared after the modal unmounts, not on close, so its content does not
  // blank out mid-exit-animation. This used to hang off the dialog's `onExited`
  // prop, which a Radix dialog never calls — so it never ran at all.
  useEffect(() => {
    if (!editModal.isMounted) setProjectToEdit(null);
  }, [editModal.isMounted]);

  const { useDeletePublishedProject, QuickEditProjectModal } = editProject;
  const deleteMutation = useDeletePublishedProject();

  const totalColumns = 7; // Name, Folders, Type, Owner, Last activity, Created, Actions

  const handleEdit = (project: ProjectListItem) => {
    setProjectToEdit(project);
    editModal.open();
  };

  const handleConfirmDelete = (project: ProjectListItem) => {
    if (!project.platformProjectId) return;
    deleteMutation.mutate(project.platformProjectId, {
      onSuccess: () => {
        notificationService.showSuccess(t('projectsList.projectsTable.projectDeleted'));
        setProjectToDelete(null);
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : t('projectsList.projectsTable.couldNotDeleteProject');
        notificationService.showError(message);
      },
    });
  };

  const isBusy = isLoading && rows.length === 0;

  return (
    <>
      <div className="grid h-full min-h-0 w-full grid-rows-[minmax(0,1fr)_auto]">
        <div className="min-h-0 overflow-auto px-section pb-section">
          <Datatable aria-busy={isBusy || undefined} className={redesignTableStyles.table}>
            {/*
              This table's right block is the heaviest, so it defines the
              shared data-region width; Name takes the rest.
            */}
            <DatatableHeader>
              <DatatableRow>
                <DatatableHead scope="col" className={TABLE_NAME_COL_NO_SIDEBAR}>
                  {t('projectsList.projectsTable.name')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.count}>
                  {t('projectsList.projectsTable.folders')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
                  {t('projectsList.projectsTable.type')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.person}>
                  {t('projectsList.projectsTable.owner')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.lastActivity}>
                  {t('projectsList.projectsTable.lastActivity')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.dateText}>
                  {t('projectsList.projectsTable.created')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
                  <span className="sr-only">{t('projectsList.projectsTable.actions')}</span>
                </DatatableHead>
              </DatatableRow>
            </DatatableHeader>
            <DatatableBody>
              {isBusy ? (
                SKELETON_ROWS.map((i) => <ProjectsTableSkeletonRow key={i} />)
              ) : rows.length === 0 ? (
                <DatatableRow>
                  <DatatableCell
                    colSpan={totalColumns}
                    className="h-20 text-center text-muted-foreground"
                  >
                    {t('projectsList.projectsTable.noProjectsFound')}
                  </DatatableCell>
                </DatatableRow>
              ) : (
                rows.map((row) => (
                  <DatatableRow
                    key={row.id}
                    interactive
                    // Cursor comes from the `interactive` variant. The slower
                    // bg-color tween on hover is deliberate — layered on top of
                    // that variant so the child-cell background animates rather
                    // than snapping.
                    className="h-[72px] motion-safe:transition-[background-color] motion-safe:duration-fast motion-safe:ease-out"
                    onClick={() => row.platformProjectId && onRowClick(row.platformProjectId)}
                  >
                    <DatatableCell>
                      <div className="flex min-w-0 items-center gap-3">
                        <TruncatedText
                          value={row.title}
                          className="text-sm font-medium text-foreground"
                        />
                      </div>
                    </DatatableCell>

                    <DatatableCell>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                          <FoldersIcon className="size-4 text-muted-foreground" />
                          {row.activity.folderCount > 0 ? row.activity.folderCount : '—'}
                        </span>
                      </div>
                    </DatatableCell>

                    <DatatableCell>
                      {(() => {
                        const { icon: Icon, label } = getProjectTypeConfig(row.type);
                        return (
                          <Badge variant="secondary" roundness="full" className="font-normal">
                            <Icon />
                            {label}
                          </Badge>
                        );
                      })()}
                    </DatatableCell>

                    <DatatableCell>
                      {/*
                        Null-owner is guarded OUTSIDE the primitive to preserve
                        pre-migration visual parity: previously we rendered
                        only an em-dash span (no avatar circle) for null
                        owners. `PreResolvedAvatarCell`'s null-name branch
                        renders `<Avatar>[empty fallback] + <span>—</span>` —
                        correct for consumers that want the avatar+em-dash
                        pattern, but not the right fit here.
                      */}
                      {row.owner ? (
                        <PreResolvedAvatarCell
                          name={row.owner.name}
                          // Upstream `ProjectOwner` widens `avatarUrl` to
                          // `string | null | undefined`; coalesce so "no
                          // avatar" is a single canonical null.
                          avatarUrl={row.owner.avatarUrl ?? null}
                          truncate
                          displayNameOverride={
                            currentUser?.id === row.owner.id
                              ? t('projectsList.projectsTable.me')
                              : undefined
                          }
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </DatatableCell>

                    <DatatableCell>
                      <span className="text-sm">
                        {formatDistanceToNow(new Date(row.activity.lastActivityAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </DatatableCell>

                    <DatatableCell>
                      <span className="text-sm">{formatShortDate(row.createdAt)}</span>
                    </DatatableCell>

                    <DatatableCell className="w-12">
                      {isWorkspaceAdmin && (
                        <ProjectRowActions
                          project={row}
                          onEdit={handleEdit}
                          onDelete={setProjectToDelete}
                          canEdit={isWorkspaceAdmin}
                          canDelete={isWorkspaceAdmin}
                        />
                      )}
                    </DatatableCell>
                  </DatatableRow>
                ))
              )}
            </DatatableBody>
          </Datatable>
        </div>

        <PaginationControls
          page={page + 1}
          totalItems={totalCount}
          pageSize={rowsPerPage}
          onPageChange={(nextPage) => onPageChange(nextPage - 1)}
          className="mt-auto p-section"
        />
      </div>

      <DeleteConfirmationModal
        open={projectToDelete !== null}
        isDeleting={deleteMutation.isPending}
        resourceType={tCommon('components.deleteConfirmationModal.project')}
        resourceName={projectToDelete?.title ?? undefined}
        warningMessage={t('projectsList.projectsTable.thisActionCannotBeUndone')}
        onCancel={() => setProjectToDelete(null)}
        onConfirmDelete={() => {
          if (projectToDelete) {
            handleConfirmDelete(projectToDelete);
          }
        }}
      />

      {editModal.isMounted && projectToEdit && (
        <QuickEditProjectModal
          key={editModal.instanceKey}
          open={editModal.isOpen}
          project={projectToEdit}
          workspaceId={workspaceId}
          onClose={editModal.close}
          onSuccess={(project) =>
            notificationService.showSuccess(
              t('projectsList.projectsTable.projectUpdated', {
                projectTitle: project.title ?? '',
              }),
            )
          }
        />
      )}
    </>
  );
}
